import React, { useState, useEffect } from 'react';
import api from '../config/api';
import { toast } from 'react-toastify';
import { FiSave, FiGlobe, FiMail, FiPhone, FiMapPin, FiImage, FiFacebook, FiTwitter, FiInstagram, FiLinkedin } from 'react-icons/fi';
import AdminNav from '../components/AdminNav';

const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('identity');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings', settings);
      toast.success('Settings saved successfully! Refresh your homepage to see changes.');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  const handleStatChange = (key, field, value) => {
    setSettings(prev => ({
      ...prev,
      statistics: {
        ...prev.statistics,
        [key]: {
          ...prev.statistics[key],
          [field]: value
        }
      }
    }));
  };

  const sections = [
    { id: 'identity', name: 'Site Identity', icon: <FiGlobe /> },
    { id: 'contact', name: 'Contact Info', icon: <FiMail /> },
    { id: 'location', name: 'Location', icon: <FiMapPin /> },
    { id: 'hero', name: 'Homepage Hero', icon: <FiImage /> },
    { id: 'statistics', name: 'Statistics', icon: <FiImage /> },
    { id: 'social', name: 'Social Media', icon: <FiFacebook /> },
    { id: 'branding', name: 'Colors & Brand', icon: <FiImage /> }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNav />
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6 flex justify-between items-center">
          <h1 className="text-3xl font-bold">Site Settings</h1>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition flex items-center space-x-2 disabled:opacity-50"
          >
            <FiSave />
            <span>{saving ? 'Saving...' : 'Save All Changes'}</span>
          </button>
        </div>

        <div className="grid md:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-xl shadow-md p-4 space-y-2">
              {sections.map(section => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full text-left px-4 py-3 rounded-lg transition flex items-center space-x-3 ${
                    activeSection === section.id
                      ? 'bg-primary-500 text-white'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  {section.icon}
                  <span>{section.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="md:col-span-3">
            <div className="bg-white rounded-xl shadow-md p-8">
              
              {/* Site Identity */}
              {activeSection === 'identity' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold mb-6">Site Identity</h2>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Site Name
                    </label>
                    <input
                      type="text"
                      value={settings.siteName}
                      onChange={(e) => handleChange('siteName', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="MathTutoring"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Site Tagline
                    </label>
                    <input
                      type="text"
                      value={settings.siteTagline}
                      onChange={(e) => handleChange('siteTagline', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="Online Private Math Classes"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Logo URL
                    </label>
                    <input
                      type="text"
                      value={settings.logoUrl || ''}
                      onChange={(e) => handleChange('logoUrl', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="https://example.com/logo.png"
                    />
                    <p className="text-sm text-gray-500 mt-1">Enter image URL or leave empty to use default</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SEO Meta Title
                    </label>
                    <input
                      type="text"
                      value={settings.metaTitle}
                      onChange={(e) => handleChange('metaTitle', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SEO Meta Description
                    </label>
                    <textarea
                      value={settings.metaDescription}
                      onChange={(e) => handleChange('metaDescription', e.target.value)}
                      rows="3"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
              )}

              {/* Contact Information */}
              {activeSection === 'contact' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold mb-6">Contact Information</h2>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FiMail className="inline mr-2" />
                      Contact Email
                    </label>
                    <input
                      type="email"
                      value={settings.contactEmail}
                      onChange={(e) => handleChange('contactEmail', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FiPhone className="inline mr-2" />
                      Contact Phone
                    </label>
                    <input
                      type="tel"
                      value={settings.contactPhone}
                      onChange={(e) => handleChange('contactPhone', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      WhatsApp Number (optional)
                    </label>
                    <input
                      type="tel"
                      value={settings.whatsappNumber || ''}
                      onChange={(e) => handleChange('whatsappNumber', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="+1234567890"
                    />
                  </div>
                </div>
              )}

              {/* Location */}
              {activeSection === 'location' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold mb-6">Business Location</h2>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address Line 1
                    </label>
                    <input
                      type="text"
                      value={settings.addressLine1}
                      onChange={(e) => handleChange('addressLine1', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address Line 2 (optional)
                    </label>
                    <input
                      type="text"
                      value={settings.addressLine2 || ''}
                      onChange={(e) => handleChange('addressLine2', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                      <input
                        type="text"
                        value={settings.city || ''}
                        onChange={(e) => handleChange('city', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                      <input
                        type="text"
                        value={settings.state || ''}
                        onChange={(e) => handleChange('state', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Zip Code</label>
                      <input
                        type="text"
                        value={settings.zipCode || ''}
                        onChange={(e) => handleChange('zipCode', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                      <input
                        type="text"
                        value={settings.country}
                        onChange={(e) => handleChange('country', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Hero Section */}
              {activeSection === 'hero' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold mb-6">Homepage Hero Section</h2>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hero Title
                    </label>
                    <input
                      type="text"
                      value={settings.heroTitle}
                      onChange={(e) => handleChange('heroTitle', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hero Subtitle
                    </label>
                    <textarea
                      value={settings.heroSubtitle}
                      onChange={(e) => handleChange('heroSubtitle', e.target.value)}
                      rows="3"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      About Section Title
                    </label>
                    <input
                      type="text"
                      value={settings.aboutTitle}
                      onChange={(e) => handleChange('aboutTitle', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      About Section Description
                    </label>
                    <textarea
                      value={settings.aboutDescription}
                      onChange={(e) => handleChange('aboutDescription', e.target.value)}
                      rows="2"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Footer Text
                    </label>
                    <textarea
                      value={settings.footerText}
                      onChange={(e) => handleChange('footerText', e.target.value)}
                      rows="2"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
              )}

              {/* Statistics */}
              {activeSection === 'statistics' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold mb-6">Homepage Statistics</h2>
                  <p className="text-gray-600 mb-4">These numbers appear on your homepage hero section</p>
                  
                  {/* Students Stat */}
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-4">Students Taught</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Value</label>
                        <input
                          type="text"
                          value={settings.statistics?.students?.value || ''}
                          onChange={(e) => handleStatChange('students', 'value', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          placeholder="500+"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Label</label>
                        <input
                          type="text"
                          value={settings.statistics?.students?.label || ''}
                          onChange={(e) => handleStatChange('students', 'label', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          placeholder="Students Taught"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Success Rate */}
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-4">Success Rate</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Value</label>
                        <input
                          type="text"
                          value={settings.statistics?.success?.value || ''}
                          onChange={(e) => handleStatChange('success', 'value', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          placeholder="95%"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Label</label>
                        <input
                          type="text"
                          value={settings.statistics?.success?.label || ''}
                          onChange={(e) => handleStatChange('success', 'label', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          placeholder="Success Rate"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Rating */}
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-4">Rating</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Value</label>
                        <input
                          type="text"
                          value={settings.statistics?.rating?.value || ''}
                          onChange={(e) => handleStatChange('rating', 'value', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          placeholder="4.9/5"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Label</label>
                        <input
                          type="text"
                          value={settings.statistics?.rating?.label || ''}
                          onChange={(e) => handleStatChange('rating', 'label', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          placeholder="Rating"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="bg-primary-500 text-white p-6 rounded-lg">
                    <h3 className="font-semibold mb-4">Preview:</h3>
                    <div className="flex items-center justify-around">
                      <div className="text-center">
                        <div className="text-2xl font-bold">{settings.statistics?.students?.value || '0'}</div>
                        <div className="text-sm text-primary-100">{settings.statistics?.students?.label || 'Label'}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold">{settings.statistics?.success?.value || '0'}</div>
                        <div className="text-sm text-primary-100">{settings.statistics?.success?.label || 'Label'}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold">{settings.statistics?.rating?.value || '0'}</div>
                        <div className="text-sm text-primary-100">{settings.statistics?.rating?.label || 'Label'}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Social Media */}
              {activeSection === 'social' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold mb-6">Social Media Links</h2>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FiFacebook className="inline mr-2 text-blue-600" />
                      Facebook URL
                    </label>
                    <input
                      type="url"
                      value={settings.facebookUrl || ''}
                      onChange={(e) => handleChange('facebookUrl', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="https://facebook.com/yourpage"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FiTwitter className="inline mr-2 text-blue-400" />
                      Twitter URL
                    </label>
                    <input
                      type="url"
                      value={settings.twitterUrl || ''}
                      onChange={(e) => handleChange('twitterUrl', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="https://twitter.com/yourhandle"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FiInstagram className="inline mr-2 text-pink-600" />
                      Instagram URL
                    </label>
                    <input
                      type="url"
                      value={settings.instagramUrl || ''}
                      onChange={(e) => handleChange('instagramUrl', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="https://instagram.com/yourhandle"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FiLinkedin className="inline mr-2 text-blue-700" />
                      LinkedIn URL
                    </label>
                    <input
                      type="url"
                      value={settings.linkedinUrl || ''}
                      onChange={(e) => handleChange('linkedinUrl', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="https://linkedin.com/company/yourcompany"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      YouTube URL
                    </label>
                    <input
                      type="url"
                      value={settings.youtubeUrl || ''}
                      onChange={(e) => handleChange('youtubeUrl', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="https://youtube.com/@yourchannel"
                    />
                  </div>
                </div>
              )}

              {/* Colors & Branding */}
              {activeSection === 'branding' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold mb-6">Colors & Branding</h2>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Primary Color
                    </label>
                    <div className="flex gap-4 items-center">
                      <input
                        type="color"
                        value={settings.primaryColor}
                        onChange={(e) => handleChange('primaryColor', e.target.value)}
                        className="w-20 h-12 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={settings.primaryColor}
                        onChange={(e) => handleChange('primaryColor', e.target.value)}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-1">Main brand color used throughout the site</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Secondary Color
                    </label>
                    <div className="flex gap-4 items-center">
                      <input
                        type="color"
                        value={settings.secondaryColor}
                        onChange={(e) => handleChange('secondaryColor', e.target.value)}
                        className="w-20 h-12 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={settings.secondaryColor}
                        onChange={(e) => handleChange('secondaryColor', e.target.value)}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-1">Used for gradients and accents</p>
                  </div>

                  <div className="mt-8 p-6 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold mb-4">Preview</h3>
                    <div className="space-y-4">
                      <div 
                        className="h-20 rounded-lg"
                        style={{ background: `linear-gradient(135deg, ${settings.primaryColor}, ${settings.secondaryColor})` }}
                      />
                      <button 
                        style={{ backgroundColor: settings.primaryColor }}
                        className="px-6 py-3 text-white rounded-lg font-semibold"
                      >
                        Sample Button
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;

